import { useLocation } from '@reach/router';
import React, { useEffect, useState } from 'react';
import {
  IPriceInfo,
  IPromoCode,
} from 'pages/newSubscription/models';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSelectedPackages,
  setPrice,
  updateSelectedPackages,
  selectMembershipOptInfo,
  setMembershipOptInfo,
  setSchoolAccountContext,
} from 'reducers/misc';
import { Spinner } from 'components/spinner/Spinner';
import { AppPath, ERROR_MESSAGE, INVALID_PROMO_CODE_MESSAGE } from 'appConstants';
import { SubscribeApi } from 'api/subscribeApi';
import ErrorMessage from 'components/errorMessage/ErrorMessage';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';
import { PricingSummaryDetailsApi } from 'components/pricingSummaryDetails/pricingSummaryDetailsApi';
import { CellPrices, OptionsGrid } from 'components/optionsGrid/OptionsGrid';
import { selectSignedIn } from 'reducers/auth';
import { selectQuotePromoCodeSlots, setQuotePromoCodeSlots } from 'reducers/misc';
import {
  applyCodeToSlots,
  droppedBySlotChange,
  expiredQuotePromoCodes,
  packagesFromGrid,
  removeCodeFromSlots,
  subjectsForPackageIds,
} from 'subscriptionOptions/promoCodeSlots';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import { useFetchOptions } from 'hooks/useFetchOptions';
import { applySelection, packageIdsInGrid, selectionToPackageIds } from 'subscriptionOptions/optionsGrid';
import { buildCandidateOrders, mapCandidatePrices } from 'subscriptionOptions/pricingRequest';
import { GridSelection, OptionsGridModel, OptionsType } from 'subscriptionOptions/types';


type ChoosePackageFormProps = {
  onSubmit: () => void;
};

export const ChoosePackageForm: React.FC<ChoosePackageFormProps> = ({
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const isSignedIn = useSelector(selectSignedIn);
  const existingMembershipOptInfo = useSelector(selectMembershipOptInfo);

  const pathname = location.pathname;
  const isSignedInAddSubscription = isSignedIn && pathname === AppPath.NEW_SUBSCRIPTION;

  const selectedPackages = useSelector(selectSelectedPackages);
  const [promoCode, setPromoCode] = useState<IPromoCode>({
    value: (existingMembershipOptInfo && existingMembershipOptInfo[0] && existingMembershipOptInfo[0].promoCodes && existingMembershipOptInfo[0].promoCodes[0]) || '',
    error: '',
  });
  const [priceInfo, setPriceInfo] = useState<IPriceInfo>({
    price: 0,
    vat: 0,
    total: 0,
    discount: 0,
    appliedDiscountType: '',
  });
  const [selectedPackageDetails, setSelectedPackageDetails] = useState<any>(!isSignedIn ? existingMembershipOptInfo : null);
  const [initPackageUpdate, setInitPackageUpdate] = useState<boolean>(true);
  const [selectedPackageUpdated, setSelectedPackageUpdated] = useState<boolean>(false);

  // Signed-in: grid from options, prices from pricing.
  const { grid, accountContext, loading: optionsLoading, error: optionsError } = useFetchOptions(
    OptionsType.ADD,
    isSignedInAddSubscription
  );
  const [gridSelection, setGridSelection] = useState<GridSelection>({});
  const [cellPrices, setCellPrices] = useState<CellPrices>({});
  const [cellPricesError, setCellPricesError] = useState<string>('');

  const [anyFreeTrialSubFlag, setAnyFreeTrialSubFlag] = useState<boolean>(false);
  const [anyFullPaidSubFlag, setAnyFullPaidSubFlag] = useState<boolean>(false);

  /** The quote's codes, one per subject. Held in the store because the promo box unmounts at step 2. */
  const promoCodeSlots = useSelector(selectQuotePromoCodeSlots);
  const [applyingPromoCode, setApplyingPromoCode] = useState<boolean>(false);
  const [promoCodeRejection, setPromoCodeRejection] = useState<string>('');
  const [droppedPromoCodes, setDroppedPromoCodes] = useState<string[]>([]);

  /** What a checked code resolves to a subject through. Signed in there is no quote, so the grid stands in. */
  const quotePackages = isSignedInAddSubscription
    ? packagesFromGrid(grid)
    : (existingMembershipOptInfo &&
        existingMembershipOptInfo[0] &&
        existingMembershipOptInfo[0].quotePackages) ||
      [];

  /**
   * The distinct codes the slots hold, for the list under the promo input. Pricing decides what
   * each one actually won, so only the code itself is carried here.
   */
  const heldPromoCodes = Object.keys(promoCodeSlots)
    .map((subjectsId: string) => promoCodeSlots[Number(subjectsId)])
    .reduce(
      (codes: { promoCode: string }[], code: string) =>
        codes.some((held) => held.promoCode.toLowerCase() === code.toLowerCase())
          ? codes
          : [...codes, { promoCode: code }],
      []
    );

  // Written on every load, null included, so a previous visit's context cannot linger.
  useEffect(() => {
    if(!grid) return;

    dispatch(setSchoolAccountContext(accountContext));
    setAnyFreeTrialSubFlag(!!accountContext && accountContext.bAnyFreeTrialSubscription);
    setAnyFullPaidSubFlag(!!accountContext && accountContext.bAnyFullPaidSubscription);
  }, [grid, accountContext, dispatch]);

  useEffect(() => {
    if(initPackageUpdate && selectedPackageDetails){
      updateSelectedPackage("oneYearPackage", 0);
      setInitPackageUpdate(false);
    }
  }, [initPackageUpdate, selectedPackageDetails]);

  useEffect(() => {
    if(isSignedIn){
      dispatch(setMembershipOptInfo(null));
    }
  }, [isSignedIn]);

  useEffect(() => {
    if(selectedPackageUpdated){
      setSelectedPackageDetails(selectedPackageDetails);
      setSelectedPackageUpdated(false);
    }
  }, [selectedPackageUpdated]);

  // Price every candidate in one call, and preselect the 1-year option as the public flow does.
  useEffect(() => {
    if(!grid) return;

    loadCellPrices(grid);

    const first = grid.subjects[0];
    if(first){
      const oneYear = first.cells.find((c) => c.package.subscriptionTypesDuration === 1) || first.cells[0];
      selectGridPackage(first.subjectsId, oneYear.packagesId, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid]);

  const loadCellPrices = async (options_grid: OptionsGridModel) => {
    const candidateIds = packageIdsInGrid(options_grid);
    setCellPricesError('');

    if(candidateIds.length === 0) return;

    const result = await PricingSummaryDetailsApi.getSchoolPricingSummaryDetails(buildCandidateOrders(candidateIds));

    if(result.data){
      setCellPrices(mapCandidatePrices(result.data, candidateIds));
    } else {
      setCellPricesError((result.error && result.error.message) || ERROR_MESSAGE);
    }
  };

  /**
   * Places a code on the subjects it covers.
   *
   * checkpromocode is the rejection signal: it refuses a code that is invalid, expired or
   * covers no package, so nothing has to be inferred from a price. A code it accepts fills the
   * slot of every subject it covers, displacing whatever was there, and pricing then reports
   * what each one actually won.
   */
  const applyQuotePromoCode = async (code: string) => {
    const trimmed = (code || '').trim();

    if(!trimmed || applyingPromoCode) return;

    setApplyingPromoCode(true);
    setPromoCodeRejection('');
    setDroppedPromoCodes([]);

    // Signed in, the school-scoped check also refuses a code this account has already used.
    const result = isSignedInAddSubscription
      ? await SubscriptionsApi.checkPromoCode(trimmed)
      : await SubscribeApi.checkPromoCode(trimmed);

    if(result.error || !result.data){
      setPromoCodeRejection((result.error && result.error.message) || ERROR_MESSAGE);
      setApplyingPromoCode(false);
      return;
    }

    const coveredSubjects = subjectsForPackageIds(result.data, quotePackages);

    // A code the server accepted but that covers none of this quote's subjects buys nothing here.
    if(coveredSubjects.length === 0){
      setPromoCodeRejection(INVALID_PROMO_CODE_MESSAGE);
      setApplyingPromoCode(false);
      return;
    }

    const nextSlots = applyCodeToSlots(promoCodeSlots, trimmed, coveredSubjects);

    setDroppedPromoCodes(droppedBySlotChange(promoCodeSlots, nextSlots));
    dispatch(setQuotePromoCodeSlots(nextSlots));
    setPromoCode({ value: '', error: '' });
    setApplyingPromoCode(false);
  };

  /** Removing a code drops it from the pricing payload; the quote itself is untouched. */
  const removeQuotePromoCode = (code: string) => {
    setPromoCodeRejection('');
    setDroppedPromoCodes([]);
    dispatch(setQuotePromoCodeSlots(removeCodeFromSlots(promoCodeSlots, code)));
  };

  const onContinue = () => {
    dispatch(
      setPrice({
        price: priceInfo.price,
        vat: priceInfo.vat,
        total: priceInfo.total,
        discount: priceInfo.discount,
        discountType: priceInfo.appliedDiscountType,
      })
    );
    onSubmit();
  };

  /* ---------- signed-in selection (grid) ---------- */

  const selectGridPackage = (subjectsId: number, packagesId: number | null, base: GridSelection = gridSelection) => {
    if(!grid) return;

    const next = applySelection(base, subjectsId, packagesId);

    if(packagesId !== null && base[subjectsId] === packagesId) return;

    setGridSelection(next);

    // Downstream steps read packageIds only.
    dispatch(updateSelectedPackages(selectionToPackageIds(next).map((id) => ({ packageIds: [id] })) as any));
  };

  /* ---------- public selection (membership options) ---------- */

  const updateSelectedPackage = (package_to_update: string, subject_index: number) => {
    let selectedPackageDetailsCopy = selectedPackageDetails;

    // A quote need not price both durations, so the one being selected may not exist.
    const packageToUpdate = selectedPackageDetailsCopy[subject_index] &&
      selectedPackageDetailsCopy[subject_index][package_to_update];

    if(!packageToUpdate) return;

    const packageSelected: boolean = packageToUpdate.selected === undefined ? true :
      !packageToUpdate.selected;

    // A quote need not price both durations, so only clear the ones it actually offered.
    if(selectedPackageDetailsCopy[subject_index]["oneYearPackage"]){
      selectedPackageDetailsCopy[subject_index]["oneYearPackage"].selected = false;
    }
    if(selectedPackageDetailsCopy[subject_index]["threeYearPackage"]){
      selectedPackageDetailsCopy[subject_index]["threeYearPackage"].selected = false;
    }
    selectedPackageDetailsCopy[subject_index][package_to_update].selected = packageSelected;

    setSelectedPackageDetails(selectedPackageDetailsCopy);
    setSelectedPackageUpdated(true);

    selectPackage({
      packageIds: selectedPackageDetailsCopy[subject_index][package_to_update].packageIds,
      type: selectedPackageDetailsCopy[subject_index][package_to_update].subscriptionType,
      price: selectedPackageDetailsCopy[subject_index][package_to_update].price,
      pricePerYear: selectedPackageDetailsCopy[subject_index][package_to_update].pricePerYear,
      subject: selectedPackageDetailsCopy[subject_index].selectedSubjectName,
      ageGroup: selectedPackageDetailsCopy[subject_index].ageGroup,
      subjectIndex: selectedPackageDetailsCopy[subject_index][package_to_update].subjectIndex
      },
      packageSelected
    );
  };

  const selectPackage = (p: any, packageSelected: boolean) => {
    let packageList: any = selectedPackages;

    if(!packageSelected){
      packageList = packageList.filter((packageObj: any) => packageObj.packageIds[0] !== p.packageIds[0]);
    } else {
      for(let i = 0; i < packageList.length; i++){
        if(packageList[i].subjectIndex === p.subjectIndex){
          packageList.splice(i, 1);
        }
      }

      packageList = [...packageList, p];
    }

    dispatch(updateSelectedPackages(packageList));
  };

  if (optionsError) {
    return <ErrorMessage message={optionsError}></ErrorMessage>;
  }

  if (isSignedInAddSubscription ? optionsLoading : !selectedPackageDetails) {
    return (
      <div
        style={{
          minHeight: ' calc(100vh - 300px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div className="PricingColRow">
      <div className="PCRowInner SmGap">
        <div className="LeftCol">
          {isSignedInAddSubscription && grid &&
            <>
              {grid.subjects.length === 0 &&
                <h3 className="GeneralMsg">There are no subscriptions available to add at the moment.</h3>
              }
              {cellPricesError &&
                <div className="ErrorTxt">{cellPricesError}</div>
              }
              <OptionsGrid
                grid={grid}
                selection={gridSelection}
                onSelect={(subjectsId, packagesId) => selectGridPackage(subjectsId, packagesId)}
                cellPrices={cellPrices}
                chargeVat={!!(accountContext && accountContext.bChargeVAT)}
              />
            </>
          }

          {!isSignedInAddSubscription && selectedPackageDetails && selectedPackageDetails.length > 0 &&
            <>
              {selectedPackageDetails.map((packageObj: any, index: number) => {
                const vatTxtAvailable =
                  (packageObj && packageObj.selectedLocationLabel && packageObj.selectedLocationLabel.toLowerCase() === "united kingdom");

                return (
                  <div className="SubDetailsBlock" key={"Package_Key_" + index}>
                    <div className="LineSpacer3"></div>
                    <h3>Your subscription options for {selectedPackageDetails[0].ageGroup} {packageObj.selectedSubjectName} are:</h3>
                    <div className="LineSpacer3"></div>

                    <div className="SubSelectOptRow">
                      {packageObj.oneYearPackage &&
                        <label className={"SubOption" + (packageObj.oneYearPackage.selected ? " Checked" : "")}>
                          <input
                            type="radio"
                            name={"subLengthOpts_" + index}
                            id={"subLengthOpts_1year_" + index}
                            className="HiddenInput"
                            checked={packageObj.oneYearPackage.selected}
                            onClick={(e: any) => updateSelectedPackage("oneYearPackage", index)}
                          />
                          <label className="CustomInput" htmlFor={'subLengthOpt1'}>
                            1 year<span>£{packageObj.oneYearPackage.price.toFixed(2)}{(vatTxtAvailable ? " + VAT" : "")}</span>
                          </label>
                        </label>
                      }
                      {packageObj.threeYearPackage &&
                        <label className={"SubOption" + (packageObj.threeYearPackage.selected ? " Checked" : "")}>
                          <input
                            type="radio"
                            name={"subLengthOpts_" + index}
                            id={"subLengthOpts_3year_" + index}
                            className="HiddenInput"
                            checked={packageObj.threeYearPackage.selected}
                            onClick={(e: any) => updateSelectedPackage("threeYearPackage", index)}
                          />
                          <label className="CustomInput" htmlFor={'subLengthOpt2'}>
                            3 years
                            <span>
                              £{packageObj.threeYearPackage.price.toFixed(2)}{(vatTxtAvailable ? " + VAT" : "")}
                              <span className="SmTxt">{packageObj.threeYearPackage.pricePerYear}</span>
                            </span>
                          </label>
                        </label>
                      }
                    </div>
                  </div>
                );
              })}
            </>
          }
        </div>
        <div className="RightCol">
          {(isSignedInAddSubscription ? !!grid : !!selectedPackageDetails) &&
            <PricingSummaryDetails
              addPromoStep={true}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              setPriceInfo={setPriceInfo}
              anyFreeTrialSub={anyFreeTrialSubFlag}
              anyFullPaidSub={anyFullPaidSubFlag}
              existingMembershipOptInfo={existingMembershipOptInfo}
              accountContext={accountContext}
              onContinue={onContinue}
              btnText="Continue"
              {...(quotePackages.length === 0
                ? {}
                : {
                    appliedPromoCodes: heldPromoCodes,
                    promoCodeSlots: promoCodeSlots,
                    quotePackages: quotePackages,
                    onPromoCodeApplied: applyQuotePromoCode,
                    onRemovePromoCode: removeQuotePromoCode,
                    droppedPromoCodes: droppedPromoCodes,
                    promoCodeRejection: promoCodeRejection,
                    expiredPromoCodes: expiredQuotePromoCodes(quotePackages),
                  })}
            />
          }
        </div>
      </div>
    </div>
  );
};
